import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BillingService, {
  CompanyProduct,
  SaveCompanyProduct,
} from '../../services/BillingService';
import SuperAdminService from '../../services/SuperAdminService';
import { MODULE_DEFINITIONS, ALL_MODULE_IDS, type ModuleId } from '../../config/moduleAccess';

export type CompanyProductsEditorHandle = {
  /** Sözleşme tarihlerini ürünlere yazarak fiyatları kaydeder. */
  save: (contractStartDate: string, contractEndDate: string) => Promise<void>;
};

interface Props {
  companyId: string;
  /** Yükleme bitince sözleşme kaydı açılabilir. */
  onReadyChange?: (ready: boolean) => void;
}

type ModuleRow = {
  id?: number;
  moduleId: ModuleId;
  label: string;
  price: number;
  vatRate: number;
  isActive: boolean;
  hasRecord: boolean;
};

type CustomRow = {
  id?: number;
  name: string;
  price: number;
  vatRate: number;
  isActive: boolean;
};

const emptyCustom = (): CustomRow => ({
  name: '',
  price: 0,
  vatRate: 20,
  isActive: true,
});

/**
 * Sözleşme penceresinin içindeki şirket ürünleri.
 * Kullanıcı yalnızca fiyat girer; başlangıç/bitiş sözleşme tarihidir ve kayıtta ürünlere yazılır.
 */
const CompanyProductsEditor = forwardRef<CompanyProductsEditorHandle, Props>(
  ({ companyId, onReadyChange }, ref) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [moduleRows, setModuleRows] = useState<ModuleRow[]>([]);
    const [customRows, setCustomRows] = useState<CustomRow[]>([]);
    const onReadyChangeRef = useRef(onReadyChange);
    onReadyChangeRef.current = onReadyChange;
    const loadFailedRef = useRef(false);

    useEffect(() => {
      let cancelled = false;
      setError(null);
      setLoading(true);
      loadFailedRef.current = false;
      onReadyChangeRef.current?.(false);
      (async () => {
        try {
          const svc = SuperAdminService.getInstance();
          let productsFailed = false;
          const [modInfo, products] = await Promise.all([
            svc.getCompanyModules(companyId).catch(() => ({ modules: null } as any)),
            BillingService.getCompanyProducts(companyId).catch(() => {
              productsFailed = true;
              return [] as CompanyProduct[];
            }),
          ]);
          if (cancelled) return;

          loadFailedRef.current = productsFailed;
          if (productsFailed) {
            setError(
              'Kayıtlı ürünler yüklenemedi. Fiyatlar bu kayıtta yazılmayacak; mevcut ürünler korunur.'
            );
          }

          const enabled: ModuleId[] =
            modInfo?.modules == null ? [...ALL_MODULE_IDS] : (modInfo.modules as ModuleId[]);

          const byModule = new Map<string, CompanyProduct>();
          products
            .filter((p) => p.kind === 'Module' && p.moduleId)
            .forEach((p) => byModule.set(p.moduleId as string, p));

          setModuleRows(
            MODULE_DEFINITIONS.filter((d) => enabled.includes(d.id)).map((def) => {
              const rec = byModule.get(def.id);
              return {
                id: rec?.id,
                moduleId: def.id,
                label: def.labelTr,
                price: rec?.price ?? 0,
                vatRate: rec?.vatRate ?? 20,
                isActive: rec?.isActive ?? true,
                hasRecord: !!rec,
              };
            })
          );

          const customs: CustomRow[] = products
            .filter((p) => p.kind === 'Custom')
            .map((p) => ({
              id: p.id,
              name: p.name,
              price: p.price,
              vatRate: p.vatRate,
              isActive: p.isActive,
            }));
          setCustomRows(customs.length ? customs : [emptyCustom()]);
        } catch (e: any) {
          if (!cancelled) setError(e?.message || 'Ürünler yüklenemedi.');
        } finally {
          if (!cancelled) {
            setLoading(false);
            onReadyChangeRef.current?.(true);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [companyId]);

    const patchModule = (moduleId: ModuleId, price: number) => {
      setModuleRows((prev) => prev.map((r) => (r.moduleId === moduleId ? { ...r, price } : r)));
    };

    const patchCustom = (index: number, patch: Partial<CustomRow>) => {
      setCustomRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const total = useMemo(() => {
      const m = moduleRows.reduce((s, r) => s + (Number(r.price) || 0), 0);
      const c = customRows.reduce((s, r) => s + (r.name.trim() ? Number(r.price) || 0 : 0), 0);
      return m + c;
    }, [moduleRows, customRows]);

    useImperativeHandle(
      ref,
      () => ({
        save: async (contractStartDate: string, contractEndDate: string) => {
          if (loadFailedRef.current) {
            throw new Error('Ürünler yüklenemediği için fiyatlar kaydedilmedi. Mevcut kayıtlar korundu.');
          }
          const start = contractStartDate || null;
          const end = contractEndDate || null;

          const moduleProducts: SaveCompanyProduct[] = moduleRows
            .filter((r) => r.hasRecord || (Number(r.price) || 0) > 0)
            .map((r) => ({
              id: r.id,
              kind: 'Module',
              moduleId: r.moduleId,
              name: r.label,
              price: Number(r.price) || 0,
              vatRate: r.vatRate || 0,
              currency: 'TRY',
              contractStartDate: start,
              contractEndDate: end,
              isActive: r.isActive,
            }));

          const customProducts: SaveCompanyProduct[] = customRows
            .filter((r) => r.name.trim())
            .map((r) => ({
              id: r.id,
              kind: 'Custom',
              name: r.name.trim(),
              price: Number(r.price) || 0,
              vatRate: r.vatRate || 0,
              currency: 'TRY',
              contractStartDate: start,
              contractEndDate: end,
              isActive: r.isActive,
            }));

          await BillingService.saveCompanyProducts(companyId, [...moduleProducts, ...customProducts]);
        },
      }),
      [companyId, moduleRows, customRows]
    );

    const priceField = (value: number, onChange: (n: number) => void, inRow = false) => (
      <TextField
        type="number"
        size="small"
        label="Fiyat"
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        inputProps={{ min: 0, step: '0.01' }}
        InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
        sx={
          inRow
            ? { flex: 1, minWidth: 0, width: { xs: 'auto', sm: 160 } }
            : { width: { xs: '100%', sm: 160 }, flexShrink: 0 }
        }
      />
    );

    return (
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: 0.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Ürünler
          </Typography>
          <Chip
            size="small"
            label={`Toplam: ${total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`}
            color="success"
            variant="outlined"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Modül ve ek ürün fiyatları. Tarih sözleşme başlangıç ve bitişinden alınır.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {moduleRows.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Bu şirkette açık modül yok. Modül açmak için Modüller ekranını kullanın.
              </Alert>
            ) : (
              moduleRows.map((r) => (
                <Box
                  key={r.moduleId}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1,
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                    {r.label}
                  </Typography>
                  {priceField(r.price, (n) => patchModule(r.moduleId, n))}
                </Box>
              ))
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
              Diğer Ürünler
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              SSL, QR Menü gibi serbest kalemler. Ad ve fiyat girin.
            </Typography>

            {customRows.map((r, idx) => (
              <Box
                key={r.id ?? `new-${idx}`}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1,
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TextField
                  size="small"
                  label="Ürün adı"
                  placeholder="Örn. SSL"
                  value={r.name}
                  onChange={(e) => patchCustom(idx, { name: e.target.value })}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: { xs: '100%', sm: 'auto' } }}>
                  {priceField(r.price, (n) => patchCustom(idx, { price: n }), true)}
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Ürünü kaldır"
                    onClick={() => setCustomRows((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}

            <Box sx={{ mt: 1 }}>
              <Button startIcon={<AddIcon />} onClick={() => setCustomRows((prev) => [...prev, emptyCustom()])}>
                Ürün Ekle
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  }
);

CompanyProductsEditor.displayName = 'CompanyProductsEditor';

export default CompanyProductsEditor;
